import type {
  ImpactReportingComplianceQuery,
  ImpactReportingConsistentOperationsQuery,
  ImpactReportingEmployeeRetentionQuery,
  ImpactReportingNewStoreOpeningsQuery,
  ImpactReportingOnboardingQuery,
} from "@src/features/reporting/widgets/results/ResultsWidgetsQuery.generated";
import {
  ComplianceType,
  StoreOpeningEvaluationStatus,
} from "@src/types.generated";

// ---------------------------------------------------------------------------
// Organization Results
// ---------------------------------------------------------------------------

export const ORGANIZATION_RESULTS_ONBOARDING_MOCK_DATA: ImpactReportingOnboardingQuery["ImpactReportingOnboarding"] =
  {
    averageDaysToCompleteNewHireTraining30DayChange: -0.8,
    months: [
      {
        month: "2025-10-01",
        averageDaysToCompleteNewHireTraining: 8.7,
      },
      {
        month: "2025-11-01",
        averageDaysToCompleteNewHireTraining: 8.1,
      },
      {
        month: "2025-12-01",
        averageDaysToCompleteNewHireTraining: 7.6,
      },
      {
        month: "2026-01-01",
        averageDaysToCompleteNewHireTraining: 7.1,
      },
      {
        month: "2026-02-01",
        averageDaysToCompleteNewHireTraining: 6.6,
      },
      {
        month: "2026-03-01",
        averageDaysToCompleteNewHireTraining: 6.2,
      },
    ],
  };

export const ORGANIZATION_RESULTS_CONSISTENT_OPERATIONS_MOCK_DATA: ImpactReportingConsistentOperationsQuery["ImpactReportingConsistentOperations"] =
  {
    totalCompletionScheduleWindows: 512,
    onTimeCompletionScheduleWindows: 443,
    totalSubmittedTaskLists: 1284,
    totalSubmittedTaskListLocations: 48,
    totalSubmittedCheckins: 2748,
    totalSubmittedCheckinLocations: 52,
    totalSubmittedTaskLists30DayChange: 4.5,
    totalSubmittedCheckins30DayChange: -2.1,
    onTimeCompletionScheduleWindows30DayChange: 3.2,
  };

export const ORGANIZATION_RESULTS_EMPLOYEE_RETENTION_MOCK_DATA: ImpactReportingEmployeeRetentionQuery["ImpactReportingEmployeeRetention"] =
  {
    averageTenure: 26,
    averageTenureMonthChange: 2,
    cohorts: {
      day30Total: 128,
      day30Retained: 112,
      day60Total: 121,
      day60Retained: 99,
      day90Total: 118,
      day90Retained: 84,
    },
    months: [
      {
        month: "2025-07-01",
        cohorts: {
          day30Total: 72,
          day30Retained: 60,
          day60Total: 69,
          day60Retained: 52,
          day90Total: 64,
          day90Retained: 42,
        },
      },
      {
        month: "2025-08-01",
        cohorts: {
          day30Total: 74,
          day30Retained: 63,
          day60Total: 71,
          day60Retained: 55,
          day90Total: 68,
          day90Retained: 47,
        },
      },
      {
        month: "2025-09-01",
        cohorts: {
          day30Total: 76,
          day30Retained: 65,
          day60Total: 74,
          day60Retained: 59,
          day90Total: 70,
          day90Retained: 50,
        },
      },
      {
        month: "2025-10-01",
        cohorts: {
          day30Total: 82,
          day30Retained: 70,
          day60Total: 79,
          day60Retained: 63,
          day90Total: 76,
          day90Retained: 55,
        },
      },
      {
        month: "2025-11-01",
        cohorts: {
          day30Total: 88,
          day30Retained: 77,
          day60Total: 85,
          day60Retained: 69,
          day90Total: 80,
          day90Retained: 58,
        },
      },
      {
        month: "2025-12-01",
        cohorts: {
          day30Total: 95,
          day30Retained: 84,
          day60Total: 91,
          day60Retained: 75,
          day90Total: 88,
          day90Retained: 64,
        },
      },
      {
        month: "2026-01-01",
        cohorts: {
          day30Total: 102,
          day30Retained: 91,
          day60Total: 98,
          day60Retained: 81,
          day90Total: null,
          day90Retained: null,
        },
      },
      {
        month: "2026-02-01",
        cohorts: {
          day30Total: 108,
          day30Retained: 98,
          day60Total: null,
          day60Retained: null,
          day90Total: null,
          day90Retained: null,
        },
      },
      {
        month: "2026-03-01",
        cohorts: {
          day30Total: null,
          day30Retained: null,
          day60Total: null,
          day60Retained: null,
          day90Total: null,
          day90Retained: null,
        },
      },
    ],
  };

// ---------------------------------------------------------------------------
// Execution Section
// ---------------------------------------------------------------------------

export const EXECUTION_MOCK_CHECKINS = {
  totalSubmittedCheckins: 82,
  totalSubmittedCheckins30DayChange: 4,
};

export const EXECUTION_MOCK_TASK_LISTS = [
  {
    name: "Opening Food Safety Checklist",
    submissions: 820,
    submissionsChange: 52 as number | null,
    locations: 30,
    onTime: 62,
    onTimeChange: 4 as number | null,
    avgFlags: 1.3 as number | null,
    flagsChange: -0.3 as number | null,
    submissionsUp: true,
    onTimeUp: true,
    flagsDown: true as boolean | null,
  },
  {
    name: "Closing Checklist",
    submissions: 805,
    submissionsChange: 38 as number | null,
    locations: 30,
    onTime: 50,
    onTimeChange: -3 as number | null,
    avgFlags: 5.5 as number | null,
    flagsChange: null as number | null,
    submissionsUp: true,
    onTimeUp: false,
    flagsDown: null as boolean | null,
  },
  {
    name: "Mid Food Safety Checklist",
    submissions: 776,
    submissionsChange: 24 as number | null,
    locations: 28,
    onTime: 58,
    onTimeChange: 6 as number | null,
    avgFlags: 4.8 as number | null,
    flagsChange: -1.2 as number | null,
    submissionsUp: true,
    onTimeUp: true,
    flagsDown: true as boolean | null,
  },
  {
    name: "AM Daily",
    submissions: 541,
    submissionsChange: -15 as number | null,
    locations: 27,
    onTime: 63,
    onTimeChange: 2 as number | null,
    avgFlags: null as number | null,
    flagsChange: null as number | null,
    submissionsUp: false,
    onTimeUp: true,
    flagsDown: null as boolean | null,
  },
];

// ---------------------------------------------------------------------------
// Retention Section
// ---------------------------------------------------------------------------

export const RETENTION_MOCK_DATA = {
  knowledgeCheckAccuracy: 91,
  knowledgeCheckAccuracy30DaysAgo: 88,
  knowledgeCheckAnswerCount: 119,
  askOpusQuestionCount: 114,
  askOpusQuestionCount30DaysAgo: 108,
};

export const RETENTION_MOCK_KNOWLEDGE_GAPS = [
  "Rainbow bowl",
  "Allergen guide",
  "Soda fountain cleaning",
];

export const RETENTION_MOCK_ASK_OPUS_THEMES = [
  "Rainbow bowl",
  "Allergen guide",
  "Soda fountain cleaning",
];

// ---------------------------------------------------------------------------
// Learning Section
// ---------------------------------------------------------------------------

export const LEARNING_MOCK_DATA = {
  assignmentPercentage: 73,
  libraryContentViews: 221,
  libraryContentViewsChange: 5,
  courseFeedbackTarget: 4.6,
  courseFeedbackRating: 4.72,
  courseFeedbackRating30DaysAgo: 4.65,
  courseFeedbackTotalRatings: 1_843,
};

export const LEARNING_MOCK_TOP_TRAININGS = [
  {
    name: "Opus 101",
    assignments: 60,
    completion: 63,
    accuracy: 93,
    completionUp: true,
    accuracyUp: null as boolean | null,
  },
  {
    name: "February 2026 Specials",
    assignments: 982,
    completion: 62,
    completionChange: 5 as number | null,
    accuracy: 96,
    accuracyChange: 2 as number | null,
    completionUp: true,
    accuracyUp: true as boolean | null,
  },
  {
    name: "Team Scheduling",
    assignments: 84,
    completion: 50,
    completionChange: -3 as number | null,
    accuracy: 93,
    accuracyChange: null as number | null,
    completionUp: false,
    accuracyUp: null as boolean | null,
  },
  {
    name: "Steritech Basics",
    assignments: 108,
    completion: 58,
    completionChange: 8 as number | null,
    accuracy: 96,
    accuracyChange: 1 as number | null,
    completionUp: true,
    accuracyUp: true as boolean | null,
  },
  {
    name: "Opus 101",
    assignments: 60,
    completion: 63,
    completionChange: 4 as number | null,
    accuracy: 93,
    accuracyChange: null as number | null,
    completionUp: true,
    accuracyUp: null as boolean | null,
  },
];

// ---------------------------------------------------------------------------
// Reach Section
// ---------------------------------------------------------------------------

export const REACH_MOCK_DATA = {
  adoptionPercentage: 78,
  adoptionPercentage30DaysAgo: 74,
  managerPercentage: 92,
  traineePercentage: 76,
};

// ---------------------------------------------------------------------------
// Organization Results Section
// ---------------------------------------------------------------------------

export const ORGANIZATION_RESULTS_PLACEHOLDER_TARGET = 60;

// ---------------------------------------------------------------------------
// Compliance
// ---------------------------------------------------------------------------

export const ORGANIZATION_RESULTS_COMPLIANCE_MOCK_DATA: ImpactReportingComplianceQuery["ImpactReportingCompliance"] =
  {
    __typename: "ImpactReportingCompliance",
    complianceTypes: [
      {
        __typename: "ImpactReportingComplianceType",
        complianceType: ComplianceType.FoodHandler,
        totalUsers: 142,
        compliantUsers: 128,
      },
      {
        __typename: "ImpactReportingComplianceType",
        complianceType: ComplianceType.FoodManager,
        totalUsers: 38,
        compliantUsers: 35,
      },
      {
        __typename: "ImpactReportingComplianceType",
        complianceType: ComplianceType.SexualHarassmentPrevention,
        totalUsers: 180,
        compliantUsers: 163,
      },
      {
        __typename: "ImpactReportingComplianceType",
        complianceType: ComplianceType.Allertrain,
        totalUsers: 95,
        compliantUsers: 82,
      },
    ],
  };

// ---------------------------------------------------------------------------
// New Store Openings
// ---------------------------------------------------------------------------

type MockOpeningLocation = {
  id: number;
  name: string;
};

const nsoMockHash = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return hash < 0 ? -hash : hash;
};

const NSO_MOCK_STATUSES: (StoreOpeningEvaluationStatus | null)[] = [
  StoreOpeningEvaluationStatus.Good,
  StoreOpeningEvaluationStatus.Ok,
  StoreOpeningEvaluationStatus.Poor,
  StoreOpeningEvaluationStatus.Good,
  null,
];

const NSO_MOCK_NOTES: Record<StoreOpeningEvaluationStatus, string[]> = {
  [StoreOpeningEvaluationStatus.Good]: [
    "Strong training prep before launch. Team members were confident and service stayed smooth.",
    "New-hire ramp was organized and opening day checklist execution was consistent.",
  ],
  [StoreOpeningEvaluationStatus.Ok]: [
    "Opening went mostly well, but onboarding pacing dipped during peak volume windows.",
    "Good launch overall; a few role handoff gaps delayed parts of the opening routine.",
  ],
  [StoreOpeningEvaluationStatus.Poor]: [
    "Multiple workflow misses during opening setup. Team needs tighter pre-shift prep.",
    "Checklist compliance was inconsistent and support coverage was not sufficient at open.",
  ],
};

const NSO_MOCK_EDITORS = [
  "Jordan Lee",
  "Sam Rivera",
  "Alex Kim",
  "Taylor Morgan",
];

const NSO_MOCK_ANCHOR_DATE = Date.parse("2026-04-20T12:00:00.000Z");

const toIso = (ms: number): string => new Date(ms).toISOString();

export const buildOrganizationResultsNewStoreOpeningsMockData = (
  locations: MockOpeningLocation[],
  maxLocations: number = 8,
): ImpactReportingNewStoreOpeningsQuery["ImpactReportingNewStoreOpenings"] =>
  [...locations]
    .sort((a, b) => {
      // Deterministic pseudo-shuffle to avoid unrealistic alpha-first openings.
      const hashA = nsoMockHash(`${a.id}:${a.name}`);
      const hashB = nsoMockHash(`${b.id}:${b.name}`);
      if (hashA === hashB) return a.id - b.id;
      return hashA - hashB;
    })
    .slice(0, maxLocations)
    .map((location, index) => {
      const openingDateMs =
        NSO_MOCK_ANCHOR_DATE - index * 7 * 24 * 60 * 60 * 1000;
      const firstMemberAddedAtMs = openingDateMs - 10 * 24 * 60 * 60 * 1000;
      const status = NSO_MOCK_STATUSES[index % NSO_MOCK_STATUSES.length];
      const editor = NSO_MOCK_EDITORS[index % NSO_MOCK_EDITORS.length];
      const notesForStatus = status ? NSO_MOCK_NOTES[status] : null;
      const evaluationNotes = notesForStatus
        ? notesForStatus[index % notesForStatus.length]
        : null;

      return {
        __typename: "ImpactReportingStoreOpening",
        id: `mock-store-opening-${location.id}`,
        locationId: location.id,
        locationName: location.name,
        firstMemberAddedAt: toIso(firstMemberAddedAtMs),
        openingDate: toIso(openingDateMs),
        evaluationStatus: status,
        evaluationNotes,
        lastEditedByName: status ? editor : null,
        lastEditedAt: status
          ? toIso(openingDateMs + 2 * 24 * 60 * 60 * 1000)
          : null,
      };
    });
